<template>
  <!-- The budget step's market card, behind rx-market-intelligence (market intelligence, suggested
       bid iteration 1: docs/MARKET_CONTEXT_SUGGESTED_BID_ITERATION_1.md, §3.3 to §3.6). It leads with
       one SUGGESTED SETUP, a bid and a minimum call length, and every number under it comes from the
       calls that priced it: one scope's delivered calls at its standard minimum over the last 7 days.
       BidMarketContextPanel is the fallback while the flag is off, and stays as it was.

       Props in, events out, nothing fetched: the host (Budget.vue) fetches E2 and passes the whole
       response, and the card emits what the buyer picks.

       Four states (§3.3):
       - priced: the setup, the range and the position, the label row, the observed numbers;
       - priced, flat (P25 and P75 on one cent, R30): the range line names the one price, the
         position has two states, and there is no P90 line (R56);
       - "No suggested bid yet": no scope cleared the gate, but the cell has calls, so the label row
         and the cell's all-minimums strip and beats line, labelled "all minimums", with no P90 line;
       - insufficient data (D3): the cell itself is too thin, and the card says so instead of hiding.

       Only a Static campaign types a bid here (I11). For RingbaRTB and Programmatic the guidance
       replaces the "Use these" button, and there is no card button, since a bid the bidder ignores
       would be the wrong thing to write. A fixed-bid Programmatic campaign's bid is in its settings,
       so it gets the position, marker and beats line a typed bid gets; a RingbaRTB or parsed-bid
       Programmatic campaign's is not on this page, so it gets none (I11).

       No number on the card is below the campaign's own bid (R58; owner, 2026-10-30: "we should
       not be showing 12.05 if buyer is bidding at 15"). Where the own bid is known and at or above
       the suggested bid, the setup, its guidance and its reason are left out, and so is every tile
       the own bid reaches; the range or flat line, the position and the observed numbers stay. The
       one setup still shown then keeps the own bid and shortens the minimum, where the own minimum
       is longer than the suggested one (R20, I19, as the list's suggestions do). No button lowers the
       bid (I13; R56).

       A campaign E1 flags as losing on conversion (R59) is led by its own row's setup where the row
       carries one (R62), a bid up to 150% of its own and/or a shorter minimum, with the one-sentence
       note on why under it, and the market's prices are left off: no tile, no P90 line, no bucket
       reason. The row's bid is never below the own bid, so R58 holds. With no setup on the row, the
       sentence stands where the market's setup and raise would be.

       The "last 7 days" in `window` is E2's window, which is always 7 days. It is NOT E1's
       weekday-dependent window: do not feed formatMarketWindow into it (MONDAY_WINDOW §11). -->
  <div
    v-if="context"
    class="market-intelligence"
    data-cy="market-intelligence-card"
  >
    <div class="mb-3">
      <strong>Live market · {{ categoryName }}</strong>
    </div>

    <!-- 1. The suggested setup, and why that minimum (I14). Absent, with the suggestion kept for
         the range, the position and the observed numbers, when the own bid is at or above the
         suggested bid and the own minimum needs no shortening (R58). Above the market's states
         because a flagged campaign's own row can carry a setup whatever the market's data (R62),
         even where the cell is too thin for a market setup (D3 below) -->
    <div
      v-if="setup"
      class="market-intelligence__section"
      data-cy="market-suggested-setup"
    >
      <div class="market-intelligence__kicker text-muted">
        Suggested setup
      </div>
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <span
          class="market-intelligence__headline mr-3"
          data-cy="text-suggested-setup"
        >
          {{ suggestedSetupText }}
        </span>
        <!-- A Static campaign's button: it raises the bid, or, where the setup keeps the typed bid,
             shortens the minimum alone (I19). It never lowers the bid (I13): a setup that would
             is not shown at all (R58). The tiles' buttons follow the same rule (R56) -->
        <b-button
          v-if="showsUseSetup"
          variant="primary"
          size="sm"
          :title="useSetupTitle"
          data-cy="button-use-suggested-setup"
          @click="useSuggestedSetup"
        >
          {{ useSetupLabel }}
        </b-button>
      </div>

      <!-- RingbaRTB and Programmatic: the bidder or the API sets the terms, so the setup is
           guidance to copy into it, worded for where the campaign's terms come from (I16) -->
      <div
        v-if="guidanceText"
        v-b-tooltip.hover
        v-clipboard
        class="market-intelligence__guidance clickable mt-2"
        title="Click to copy"
        :data-copy-text="guidanceText"
        data-copy-success="Copied"
        data-cy="text-setup-guidance"
        @click="onGuidanceCopied"
      >
        <code class="market-intelligence__guidance-text">{{ guidanceText }}</code>
        <font-awesome-icon
          :icon="['fa', 'copy']"
          class="ml-2 text-muted"
        />
      </div>

      <b-form-text
        v-if="minimumReasonText"
        class="mt-1"
        data-cy="text-minimum-reason"
      >
        {{ minimumReasonText }}
      </b-form-text>

      <!-- R62: the setup is the campaign's own row's, not the market's, so there is no bucket to
           give a reason from. The note says why on the campaign's own terms, once, here -->
      <b-form-text
        v-if="setup.fromRow"
        class="mt-1"
        data-cy="text-conversion-advice"
      >
        {{ conversionAdviceSentence }}
      </b-form-text>
    </div>

    <!-- D3: no scope cleared the gate, and the cell has under 30 delivered calls on 3 days at every
         minimum. A hidden card would be indistinguishable from a broken one. Not when the row's own
         setup leads the card (R62): "No suggested bid yet" under a suggested setup would be two
         answers, and there are no market numbers to draw under it either way. The headline stands
         alone: buyer copy never says a market lacks activity (R64) -->
    <div
      v-if="isInsufficientData && !setup"
      class="market-intelligence__section"
      data-cy="market-insufficient-data"
    >
      <div class="market-intelligence__headline">
        No suggested bid yet
      </div>
      <!-- R59: the campaign's own row can flag it whatever the market's data, and the list says so;
           the card says the same here rather than only "No suggested bid yet" -->
      <p
        v-if="losesOnConversion"
        class="mb-0 mt-2"
        data-cy="text-conversion-advice"
      >
        {{ conversionAdviceSentence }}
      </p>
    </div>

    <!-- Everything priced on the market: nothing of it in the D3 state, with or without a row setup -->
    <template v-else-if="!isInsufficientData">
      <div
        v-if="!setup && !suggestion"
        class="market-intelligence__section"
        data-cy="market-no-suggestion"
      >
        <div class="market-intelligence__headline">
          No suggested bid yet
        </div>
        <!-- R59: the conversion advice stands on its own where there is no position to hang it on -->
        <p
          v-if="losesOnConversion"
          class="mb-0 mt-2"
          data-cy="text-conversion-advice"
        >
          {{ conversionAdviceSentence }}
        </p>
      </div>

      <!-- 2. The Competitive range, and where the typed bid sits in it (R1, R3) -->
      <div
        v-if="suggestion"
        class="market-intelligence__section"
        data-cy="market-competitive-range"
      >
        <div
          v-if="isFlat"
          data-cy="text-flat-market"
        >
          {{ flatMarketText }}
        </div>
        <template v-else-if="competitiveRange">
          <div class="market-intelligence__kicker text-muted">
            Competitive range
          </div>
          <div
            class="market-intelligence__range"
            data-cy="text-competitive-range"
          >
            {{ formatCurrency(competitiveRange.lowInUSD) }}–{{ formatCurrency(competitiveRange.highInUSD) }}
          </div>
        </template>

        <p
          v-if="position"
          class="mb-0 mt-2"
          data-cy="text-bid-position"
        >
          <b-badge
            :variant="positionVariant"
            class="mr-1"
            data-cy="badge-bid-position"
          >
            {{ positionLabel }}
          </b-badge>
          <span>{{ positionText }}</span>
        </p>
        <!-- R59 with no own bid to position (a dynamic campaign with no E1 row or no bids): the advice
             alone, unless a setup above already carries it (R62) -->
        <p
          v-else-if="losesOnConversion && !setup"
          class="mb-0 mt-2"
          data-cy="text-conversion-advice"
        >
          {{ conversionAdviceSentence }}
        </p>
      </div>

      <!-- 3. The label row: buyer competition today; Slice C adds demand, volume and the trends -->
      <div
        v-if="marketActivityText"
        class="market-intelligence__section"
        data-cy="market-labels"
      >
        <span class="market-intelligence__label">{{ marketActivityText }}</span>
      </div>

      <!-- 4. What the numbers above are read from, labelled with the calls behind them (I12) -->
      <div
        class="market-intelligence__section mb-0"
        data-cy="market-observed"
      >
        <div
          class="market-intelligence__kicker text-muted mb-2"
          data-cy="text-observed-label"
        >
          {{ observedLabel }}
        </div>

        <div class="market-intelligence__bar mb-2">
          <div
            class="market-intelligence__band"
            :style="bandStyle"
          />
          <div
            v-for="percentile in ticks"
            :key="percentile.label"
            class="market-intelligence__tick"
            :style="{ left: `${positionPercent(percentile.bid)}%` }"
          />
          <div
            v-if="ownBid"
            class="market-intelligence__marker text-warning"
            :style="{ left: `${positionPercent(ownBid)}%` }"
          />
        </div>

        <!-- Spread across the strip in percentile order rather than at their own positions, which
             run into each other at narrow widths; a flat strip has no spread to label -->
        <div class="mb-2">
          <div
            v-if="!isObservedFlat"
            class="market-intelligence__scale-percentiles"
          >
            <span
              v-for="percentile in percentiles"
              :key="percentile.label"
              class="market-intelligence__scale-label text-muted"
            >
              {{ formatCurrency(percentile.bid) }} {{ percentile.label }}
            </span>
          </div>
          <div
            v-if="ownBid"
            class="market-intelligence__scale-you"
          >
            <span
              class="market-intelligence__scale-label market-intelligence__scale-label--you text-warning"
              :style="youLabelStyle"
            >
              {{ formatCurrency(ownBid) }} you
            </span>
          </div>
          <!-- The top of the market, the price the suggested bid sits $2.50 above (R61), so it
               shows beside every suggestion, in a flat cell too; none with no suggestion (R26)
               or for a campaign losing on conversion (R59, R62) -->
          <div
            v-if="frontierWinningBid !== null"
            class="market-intelligence__scale-frontier text-muted"
            data-cy="market-frontier-line"
          >
            9 in 10 winning bids were {{ formatCurrency(frontierWinningBid) }} or less. Bid above it to lead this market.
          </div>
        </div>

        <b-form-text
          v-if="beatsPercent !== null"
          class="mb-3"
          data-cy="text-beats"
        >
          Your {{ formatCurrency(ownBid) }} would have beaten about
          <strong>{{ beatsPercent }}%</strong> of {{ beatsPopulationText }} in the {{ window }}.
        </b-form-text>

        <!-- The cards, with no calls/day estimate (R15). Tiers on one cent collapse into one card,
             as the panel's do: two cards offering one bid would read as two choices. A card the own
             bid reaches is left out (R58), so no card names a lower bid, and only a Static campaign
             gets their buttons (I11; I13; R56) -->
        <div
          v-if="singleTier"
          class="market-intelligence__tier mb-2"
          data-cy="tier-suggested-bid"
        >
          <div class="market-intelligence__tier-name text-muted">
            Suggested bid
          </div>
          <div class="market-intelligence__tier-bid">
            {{ formatCurrency(singleTier.bidInUSD) }}
          </div>
          <b-form-text class="mb-2 mt-0">
            {{ getTierEstimateText(singleTier) }}
          </b-form-text>
          <b-button
            v-if="showsTierButton(singleTier)"
            variant="outline-secondary"
            size="sm"
            data-cy="button-use-tier-suggested-bid"
            @click="useTier(singleTier)"
          >
            Use {{ formatCurrency(singleTier.bidInUSD) }}
          </b-button>
        </div>

        <b-row v-else-if="displayTiers.length">
          <b-col
            v-for="tier in displayTiers"
            :key="tier.key"
            :md="12 / displayTiers.length"
            class="mb-2"
          >
            <div
              class="market-intelligence__tier d-flex flex-column"
              :class="{ 'market-intelligence__tier--highlighted': tier.isSuggested }"
              :data-cy="`tier-${tier.key.toLowerCase()}`"
            >
              <div class="market-intelligence__tier-name text-muted">
                {{ getTierText(tier.key) }}
                <b-badge
                  v-if="tier.isSuggested"
                  variant="primary"
                  class="ml-1"
                >
                  Suggested
                </b-badge>
              </div>
              <div class="market-intelligence__tier-bid">
                {{ formatCurrency(tier.bidInUSD) }}
              </div>
              <b-form-text class="mb-2 mt-0">
                {{ getTierEstimateText(tier) }}
              </b-form-text>
              <b-button
                v-if="showsTierButton(tier)"
                variant="outline-secondary"
                size="sm"
                block
                class="mt-auto"
                :data-cy="`button-use-tier-${tier.key.toLowerCase()}`"
                @click="useTier(tier)"
              >
                Use {{ formatCurrency(tier.bidInUSD) }}
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>
    </template>

    <!-- 5. The footer: the one scope every number came from (R26), and its window -->
    <b-form-text
      class="mt-2"
      data-cy="market-intelligence-footer"
    >
      Based on {{ scopeText }}, {{ window }}.
    </b-form-text>
    <b-form-text v-if="!isInsufficientData">
      Directional, not guaranteed.
    </b-form-text>
  </div>
</template>

<script>
import formatCurrency from '@/utils/formatCurrency'
import { USAGE_EVENTS, usageRoutingOf } from '@/utils/usageEventKeys'
import { campaignRoutingTypes, convertCurrencyStringToNumber } from '@/store/modules/campaign.store'
import {
  CONVERSION_ADVICE_SENTENCE,
  estimateBeatsPercent,
  getBadgeVariantForBidPosition,
  getBidPositionText,
  getCompetitiveRange,
  getConfiguredProgrammaticTerms,
  getFlatMarketText,
  getMarketActivityText,
  getMinimumReasonText,
  getMinimumText,
  getOfferedSuggestion,
  getPositionForBid,
  getPositionText,
  getScopeText,
  getSetupGuidanceText,
  getSuggestedSetupText,
  isFlatSuggestion,
  toCents
} from '@/store/modules/market-context.store'

const tierTexts = {
  Entry: 'Entry',
  Competitive: 'Competitive',
  FrontRunner: 'Front-runner'
}

// How close to an end of the strip the "you" label has to get before it stops being centred on the
// marker and anchors to that edge instead
const EDGE_ANCHOR_PERCENT = 15

export default {
  name: 'MarketIntelligenceCard',

  props: {
    // E2's response for the market, whole (marketContext/marketIntelligenceContext): the card reads
    // `suggestion`, `scope` and `insufficientData`, and, with no suggestion, the cell's all-minimums
    // percentiles, curve and activity label
    context: {
      type: Object,
      default: null
    },

    categoryName: {
      type: String,
      default: ''
    },

    // For the usage events only (docs/MARKET_CONTEXT_USAGE_ANALYTICS.md)
    categoryId: {
      type: [String, Number],
      default: null
    },

    // The footer's scope names them; the ids stand in while these are empty
    countryName: {
      type: String,
      default: ''
    },

    languageName: {
      type: String,
      default: ''
    },

    // Straight from the currency input, so it can be a string like "$38.00". Read on a Static
    // campaign only: on RingbaRTB the input is disabled, and Programmatic has none (I11); a
    // Programmatic campaign's own bid comes from programmaticBidSettings instead
    bid: {
      type: [Number, String],
      default: null
    },

    // A CPA-only category bills on conversions and has no minimum, so its setup is a bid alone
    isCpaOnly: {
      type: Boolean,
      default: false
    },

    // The minimum the buyer has typed, null while Minimum Duration is off. It never re-prices the
    // suggestion (R20): the market's numbers are the same whatever is typed. It says, on "Use
    // these", whether the minimum changes too, and, with a typed bid at or above the suggested bid,
    // whether a setup that shortens the minimum is still worth showing (R58)
    minimumDurationSeconds: {
      type: Number,
      default: null
    },

    // E2's window, always 7 days
    window: {
      type: String,
      default: 'last 7 days'
    },

    // Static gets "Use these"; RingbaRTB and Programmatic get guidance (§3.6)
    routingType: {
      type: String,
      default: campaignRoutingTypes.Static
    },

    // A Programmatic campaign's bidSettings: isBidDynamic and conversionCallLengthDynamic say which
    // terms its API returns and which are fixed in its settings (I16), and staticBidAmount and
    // conversionCallLengthInSeconds are the fixed ones, the campaign's own terms on this page (R58)
    programmaticBidSettings: {
      type: Object,
      default: null
    },

    // The campaign's own E1 row on the edit page (MarketContextAPI.getCampaignMarketPositions; the row
    // the Campaigns list shows), null on create and until the rows load. Three things come off it: a
    // RingbaRTB or returned-bid Programmatic campaign's own terms (currentTerms, read off its pings),
    // which this page has no other source for (F5's follow-up); losesOnConversion (R59), which takes
    // the market's setup and raise off the card; and, on a flagged row, the conversion-compensating
    // setup it may carry (suggestedSetup, R62), which the card leads with instead
    marketPosition: {
      type: Object,
      default: null
    }
  },

  computed: {
    suggestion() {
      return this.context?.suggestion || null
    },

    // D3. A cell with nothing to draw counts too, whatever the flag says: the backend reports one
    // as insufficient data anyway
    isInsufficientData() {
      return !this.suggestion
        && (this.context?.insufficientData === true || this.context?.winningBidP25InUSD == null)
    },

    isStatic() {
      return this.routingType === campaignRoutingTypes.Static
    },

    // The bid typed on this step, which only a Static campaign has (I11)
    typedBid() {
      return this.isStatic ? (convertCurrencyStringToNumber(this.bid) || null) : null
    },

    // A Programmatic campaign's bid and minimum as fixed in its settings: the bid unless its API
    // returns one (a CPA-only category forces it static, I42), the call length unless its API
    // returns that. A term the API returns is not on this page, so it reads as none: the card then
    // neither compares on it nor advises on it
    configuredProgrammaticTerms() {
      return getConfiguredProgrammaticTerms(this.routingType, this.programmaticBidSettings, { isCpaOnly: this.isCpaOnly })
    },

    // A Programmatic campaign's call length as fixed in its settings, null where its API returns it
    configuredProgrammaticMinimum() {
      const settings = this.programmaticBidSettings

      if (this.routingType !== campaignRoutingTypes.Programmatic || !settings || settings.conversionCallLengthDynamic === true) {
        return null
      }

      const callLength = Number(settings.conversionCallLengthInSeconds)

      return callLength > 0 ? callLength : null
    },

    // What the campaign bids now, as this page knows it (R58): a Static campaign's typed bid and
    // minimum; a Programmatic campaign's settings, each term its API returns read instead off its E1
    // row (currentTerms, from its pings); a RingbaRTB campaign's E1 row. Null on create for the dynamic
    // routing types, and for a dynamic campaign that placed no bids in E1's window: the card then shows
    // the market's setup as it would to a new campaign
    ownTerms() {
      if (this.isStatic) {
        return this.typedBid ? { bidInUSD: this.typedBid, minimumSeconds: this.minimumDurationSeconds } : null
      }

      const reported = this.marketPosition?.currentTerms || null
      const bidInUSD = this.configuredProgrammaticTerms?.bidInUSD ?? reported?.bidInUSD ?? null

      if (!bidInUSD) {
        return null
      }

      return { bidInUSD, minimumSeconds: this.configuredProgrammaticMinimum ?? reported?.minimumSeconds ?? null }
    },

    // R59: E1 flagged the campaign as losing on conversion rather than on price. Edit page only
    losesOnConversion() {
      return this.marketPosition?.losesOnConversion === true
    },

    conversionAdviceSentence() {
      return CONVERSION_ADVICE_SENTENCE
    },

    ownBid() {
      return this.ownTerms?.bidInUSD ?? null
    },

    // A CPA-only category gets a bid only (I5), whatever the suggestion carries
    showsMinimum() {
      return !this.isCpaOnly && this.suggestion?.minimumSeconds != null
    },

    suggestedMinimumSeconds() {
      return this.showsMinimum ? this.suggestion.minimumSeconds : null
    },

    // The setup the card leads with (R58). The market's, while the own bid is unknown or below the
    // suggested bid. Once the own bid reaches it, in cents with a tie counting upward: the own bid
    // with the suggested minimum when the own minimum is longer (the list's keep-the-bid suggestion,
    // R20, I19), otherwise none, since no minimum counts as below (R13) and a setup is never a
    // longer minimum (I19). With no setup the section is absent; the suggestion still prices the
    // range, the position and the observed numbers.
    //
    // A campaign losing on conversion (R59) is handed its own row's setup instead (R62), the one the
    // list shows as its chip: never the market's, since that campaign lost on conversion rather than
    // on price. None where the row carries none, so no button and no guidance either. The row's bid
    // is never below the own bid as the row was measured, so R58 holds; if the buyer has typed a bid
    // that reaches it since, a raise has nothing left to offer, and a shorter minimum is offered at
    // the typed bid, never a lower one (I13, I19). A CPA-only category has no minimum, so the setup
    // is its bid alone (I5)
    setup() {
      if (this.losesOnConversion) {
        const row = this.marketPosition?.suggestedSetup

        if (!row) {
          return null
        }

        const minimumSeconds = this.isCpaOnly ? null : (row.minimumSeconds ?? null)
        const keepsBid = this.ownBid !== null && toCents(row.bidInUSD) <= toCents(this.ownBid)

        if (keepsBid && !(row.changesMinimum === true && minimumSeconds != null)) {
          return null
        }

        return { bidInUSD: keepsBid ? this.ownBid : row.bidInUSD, minimumSeconds, keepsBid, fromRow: true }
      }

      if (!this.suggestion) {
        return null
      }

      if (!this.ownBidReaches(this.suggestion.bidInUSD)) {
        return { bidInUSD: this.suggestion.bidInUSD, minimumSeconds: this.suggestedMinimumSeconds, keepsBid: false }
      }

      const ownMinimum = this.ownTerms.minimumSeconds
      const shortensMinimum = this.suggestedMinimumSeconds != null && ownMinimum != null && ownMinimum > this.suggestedMinimumSeconds

      return shortensMinimum
        ? { bidInUSD: this.ownBid, minimumSeconds: this.suggestedMinimumSeconds, keepsBid: true }
        : null
    },

    // Whether the setup carries a minimum: the market's, when its suggestion has one and the category
    // bills on it (showsMinimum), or the row's own (R62), which names its own and not the market's
    setupShowsMinimum() {
      return this.setup?.minimumSeconds != null
    },

    suggestedSetupText() {
      return getSuggestedSetupText(this.setup)
    },

    // A Static campaign's "Use these": every setup shown either raises the bid or keeps it and
    // shortens the minimum (R58), so none lowers it (I13)
    showsUseSetup() {
      return this.isStatic && Boolean(this.setup)
    },

    useSetupLabel() {
      return this.setupShowsMinimum ? 'Use these' : `Use ${formatCurrency(this.setup.bidInUSD)}`
    },

    // "Sets Bid Amount to $50.05 and Minimum Duration to 90 seconds"; the minimum alone where the
    // setup keeps the typed bid
    useSetupTitle() {
      const minimum = this.setupShowsMinimum && this.minimumDurationSeconds !== this.setup.minimumSeconds
        ? `Minimum Duration to ${this.setup.minimumSeconds} seconds`
        : ''

      if (this.setup.keepsBid) {
        return `Sets ${minimum}`
      }

      const bid = `Sets Bid Amount to ${formatCurrency(this.setup.bidInUSD)}`

      return minimum ? `${bid} and ${minimum}` : bid
    },

    guidanceText() {
      if (!this.setup || this.isStatic) {
        return ''
      }

      return getSetupGuidanceText({
        routingType: this.routingType,
        bidInUSD: this.setup.bidInUSD,
        minimumSeconds: this.setup.minimumSeconds,
        isBidDynamic: this.programmaticBidSettings?.isBidDynamic === true,
        isCallLengthDynamic: this.programmaticBidSettings?.conversionCallLengthDynamic === true,
        isCpaOnly: this.isCpaOnly
      })
    },

    // Empty without a bucket: a CPA-only category, and a standard the category's longest minimum
    // capped (I6), whose bucket would explain a minimum the card does not suggest. Empty for the
    // row's own setup too (R62): it has no bucket, and the note under it says why instead
    minimumReasonText() {
      return this.showsMinimum && !this.setup?.fromRow ? getMinimumReasonText(this.suggestion.minimumBucket) : ''
    },

    competitiveRange() {
      return getCompetitiveRange(this.suggestion)
    },

    isFlat() {
      return isFlatSuggestion(this.suggestion)
    },

    flatMarketText() {
      return getFlatMarketText(this.suggestion)
    },

    // Where the own bid sits (R3): a Static campaign's typed bid, a fixed-bid Programmatic
    // campaign's configured one
    position() {
      return this.ownBid === null ? null : getPositionForBid(this.ownBid, this.suggestion)
    },

    positionLabel() {
      return getBidPositionText(this.position)
    },

    positionVariant() {
      return getBadgeVariantForBidPosition(this.position)
    },

    // The note rides under the position sentence on a flagged card (R59), unless the row's own setup
    // above already carries it (R62): one card says it once
    positionText() {
      return getPositionText(this.position, this.suggestion, {
        losesOnConversion: this.losesOnConversion,
        withNote: !this.setup?.fromRow
      })
    },

    // The serving scope's label with a suggestion; the cell's, which is where the all-minimums
    // strip comes from, without one
    marketActivityText() {
      return getMarketActivityText(this.suggestion ? this.suggestion.marketActivity : this.context.marketActivity)
    },

    // Where the observed numbers come from: the suggestion's pricing calls, or, with no suggestion,
    // the cell's calls at every minimum (R26)
    observedSource() {
      return this.suggestion || this.context
    },

    // "at a 90-second minimum", "at minimums of 180 seconds or longer", "at all minimums"; nothing
    // in a CPA-only category, whose calls have no minimum
    minimumText() {
      return getMinimumText(this.suggestion ? this.suggestion.pricedOn : null)
    },

    observedLabel() {
      return `Observed: winning bids${this.minimumText ? ` ${this.minimumText}` : ''}`
    },

    beatsPopulationText() {
      return `winning bids${this.minimumText ? ` ${this.minimumText}` : ''}`
    },

    percentiles() {
      return [
        { label: 'P25', bid: this.observedSource.winningBidP25InUSD },
        { label: 'P50', bid: this.observedSource.winningBidP50InUSD },
        { label: 'P75', bid: this.observedSource.winningBidP75InUSD }
      ]
    },

    isObservedFlat() {
      return toCents(this.observedSource.winningBidP25InUSD) === toCents(this.observedSource.winningBidP75InUSD)
    },

    // In a flat strip the three ticks sit on one spot, where they would only cut the dot in half
    ticks() {
      return this.isObservedFlat ? [] : this.percentiles
    },

    // The strip spans half an interquartile range beyond P25 and P75 on each side
    scale() {
      const p25 = this.observedSource.winningBidP25InUSD
      const p75 = this.observedSource.winningBidP75InUSD
      const padding = Math.max((p75 - p25) * 0.5, 1)

      return { min: p25 - padding, max: p75 + padding }
    },

    bandStyle() {
      // A zero-width band reads as a missing bar: a flat strip draws a dot on its price instead
      if (this.isObservedFlat) {
        return {
          left: `${this.positionPercent(this.observedSource.winningBidP50InUSD)}%`,
          width: '10px',
          transform: 'translateX(-50%)'
        }
      }

      const left = this.positionPercent(this.observedSource.winningBidP25InUSD)

      return {
        left: `${left}%`,
        width: `${this.positionPercent(this.observedSource.winningBidP75InUSD) - left}%`
      }
    },

    youLabelStyle() {
      const percent = this.positionPercent(this.ownBid)

      if (percent <= EDGE_ANCHOR_PERCENT) {
        return { left: 0 }
      }

      if (percent >= 100 - EDGE_ANCHOR_PERCENT) {
        return { right: 0 }
      }

      return { left: `${percent}%`, transform: 'translateX(-50%)' }
    },

    // P90 of the pricing calls, the top of the market, and since R61 (owner, 2026-10-31) the
    // price the suggested bid sits $2.50 above, so the line is the headline's reason and shows
    // beside every suggestion. It was hidden where P90 was the P75 the suggestion sat a step above
    // (R27) and in a flat cell, where "Bid above $15.00 to lead" under a $12.05 headline was a
    // second, higher suggestion (R56); with the headline above P90 neither holds. No suggestion,
    // no line (R26). None for a campaign losing on conversion either (R59, R62): P90 is the market's
    // price, and "bid above it to lead" would sit under a headline that is the row's own setup, a
    // second and different bid
    frontierWinningBid() {
      const p90 = this.suggestion?.winningBidP90InUSD

      return typeof p90 === 'number' && !this.losesOnConversion ? p90 : null
    },

    // Read off the same calls' curve as the position, so the two are one fact (R29)
    beatsPercent() {
      return estimateBeatsPercent(this.ownBid, this.observedSource)
    },

    // One card per price, the suggested tier kept, otherwise the higher one, as the panel does it
    priceTiers() {
      const cardsByCents = new Map()

      for (const tier of this.suggestion?.tiers || []) {
        const kept = cardsByCents.get(toCents(tier.bidInUSD))

        if (!kept?.isSuggested) {
          cardsByCents.set(toCents(tier.bidInUSD), tier)
        }
      }

      return [...cardsByCents.values()]
    },

    // The cards above the own bid (R58): a $40 bidder in a $25 / $35 / $50 market sees the
    // Front-runner's alone, and a $15 bidder in a flat $12 cell none. None at all for a campaign
    // losing on conversion (R59, R62): each card is a market price to use, and that campaign leads
    // with its own row's setup, which is not one of them
    displayTiers() {
      if (this.losesOnConversion) {
        return []
      }

      return this.priceTiers.filter(tier => !this.ownBidReaches(tier.bidInUSD))
    },

    // The one card of a market whose tiers land on one price, when the own bid is under it
    singleTier() {
      return this.priceTiers.length === 1 && this.displayTiers.length === 1 ? this.displayTiers[0] : null
    },

    scopeText() {
      return getScopeText(this.context.scope, {
        categoryName: this.categoryName,
        countryName: this.countryName,
        languageName: this.languageName
      })
    },

    // The usage events' name for what the card shows (docs/MARKET_CONTEXT_USAGE_ANALYTICS.md): the
    // first of these that holds. Null while the card has no context, and so renders nothing
    usageState() {
      if (!this.context) {
        return null
      }

      if (this.setup) {
        return 'setup'
      }

      if (this.isInsufficientData) {
        return 'insufficient_data'
      }

      if (this.losesOnConversion) {
        return 'conversion_note'
      }

      return this.suggestion ? 'own_bid_reaches' : 'no_suggestion'
    },

    // What the card offers, whatever bid is typed, which the events compare a bid with
    offeredSuggestion() {
      return getOfferedSuggestion({ suggestion: this.suggestion, marketPosition: this.marketPosition, isCpaOnly: this.isCpaOnly })
    }
  },

  watch: {
    // Once per state, so typing a bid that moves the card from one state to another counts each
    usageState: {
      immediate: true,
      handler(state) {
        if (!state) {
          return
        }

        this.$trackOnce(USAGE_EVENTS.marketCardShown.name, state, {
          page: this.$route?.name,
          routing: usageRoutingOf(this.routingType),
          state,
          setupFrom: this.setup ? (this.setup.fromRow ? 'row' : 'market') : 'none',
          losesOnConversion: this.losesOnConversion,
          ownBidInUSD: this.ownBid,
          suggestedBidInUSD: this.offeredSuggestion?.bidInUSD ?? null,
          suggestedMinimumSeconds: this.offeredSuggestion?.minimumSeconds ?? null,
          categoryId: this.categoryId
        })
      }
    }
  },

  methods: {
    formatCurrency,

    // Whether the own bid is at or above a price, compared in cents with a tie counting upward
    // (I13; R58). False while the own bid is unknown: nothing typed, or a campaign whose bid its
    // bidder returns
    ownBidReaches(bidInUSD) {
      return this.ownBid !== null && toCents(this.ownBid) >= toCents(bidInUSD)
    },

    // A card's "Use $X": a Static campaign only (I11). Every card shown is above the own bid (R58),
    // so none of the buttons lowers it or leaves it as it is (I13; R56)
    showsTierButton() {
      return this.isStatic
    },

    getTierText(tierKey) {
      return tierTexts[tierKey] || tierKey
    },

    // No calls/day estimate on the card (R15), only the curve read at the card's own price
    getTierEstimateText(tier) {
      return `beats ~${tier.estimatedWinRatePercentage}% of winning bids`
    },

    positionPercent(value) {
      const percent = ((value - this.scale.min) / (this.scale.max - this.scale.min)) * 100

      return Math.min(100, Math.max(0, percent))
    },

    // "Use these" fills Bid Amount and turns Minimum Duration on at the suggested minimum (R10), the
    // bid being the typed one where the setup keeps it (I19); a CPA-only category's "Use $X" fills
    // the bid alone
    //
    // Each button names itself to the host, which keeps what it set for the save's usage event
    useSuggestedSetup() {
      this.trackUse({ target: 'setup', bidInUSD: this.setup.bidInUSD, minimumSeconds: this.setupShowsMinimum ? this.setup.minimumSeconds : null })

      if (this.setupShowsMinimum) {
        this.$emit('select-setup', { bid: this.setup.bidInUSD, minimumSeconds: this.setup.minimumSeconds }, 'setup')

        return
      }

      this.$emit('select-bid', this.setup.bidInUSD, 'setup')
    },

    // A card's "Use $X": the bid alone
    useTier(tier) {
      this.trackUse({ target: 'tile', bidInUSD: tier.bidInUSD, minimumSeconds: null })
      this.$emit('select-bid', tier.bidInUSD, 'tile')
    },

    trackUse({ target, bidInUSD, minimumSeconds }) {
      this.$track(USAGE_EVENTS.marketCardUseClicked.name, {
        page: this.$route?.name,
        routing: usageRoutingOf(this.routingType),
        target,
        setsMinimum: minimumSeconds != null,
        previousBidInUSD: this.ownBid,
        bidInUSD,
        minimumSeconds
      })
    },

    // v-clipboard copies on the same click
    onGuidanceCopied() {
      this.$track(USAGE_EVENTS.marketGuidanceCopied.name, {
        page: this.$route?.name,
        routing: usageRoutingOf(this.routingType)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.market-intelligence {
  padding: 1rem;
  border: 1px solid $violet-light;
  border-radius: 0.25rem;
}

.market-intelligence__section {
  margin-bottom: 1rem;
}

.market-intelligence__kicker {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.market-intelligence__headline {
  font-size: 1.25rem;
  font-weight: 700;
}

.market-intelligence__range {
  font-size: 1rem;
  font-weight: 600;
}

.market-intelligence__guidance {
  display: inline-flex;
  align-items: center;
}

.market-intelligence__guidance-text {
  white-space: normal;
}

.market-intelligence__label {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border: 1px solid $violet-light;
  border-radius: 1rem;
  font-size: 0.75rem;
}

.market-intelligence__bar {
  position: relative;
  height: 10px;
  border-radius: 5px;
  background-color: $violet-dark;
}

.market-intelligence__band {
  position: absolute;
  top: 0;
  height: 10px;
  border-radius: 5px;
  background-color: rgba($blue, 0.45);
}

.market-intelligence__tick {
  position: absolute;
  top: -5px;
  transform: translateX(-50%);
  width: 2px;
  height: 20px;
  background-color: $grey-lighter;
}

.market-intelligence__marker {
  position: absolute;
  top: -8px;
  transform: translateX(-50%);
  width: 3px;
  height: 26px;
  border-radius: 1px;
  // Inherits Bootstrap's text-warning color so it matches the "you" label
  background-color: currentColor;
}

.market-intelligence__scale-percentiles {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.market-intelligence__scale-frontier {
  font-size: 0.75rem;
  line-height: 1.125rem;
  text-align: center;
}

.market-intelligence__scale-label {
  font-size: 0.75rem;
  line-height: 1.125rem;
  white-space: nowrap;
}

.market-intelligence__scale-you {
  position: relative;
  height: 1.125rem;
}

.market-intelligence__scale-label--you {
  position: absolute;
  top: 0;
}

.market-intelligence__tier {
  padding: 0.75rem;
  border: 1px solid $violet-light;
  border-radius: 0.25rem;
  height: 100%;
}

.market-intelligence__tier--highlighted {
  border-color: $blue;
}

.market-intelligence__tier-name {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.market-intelligence__tier-bid {
  font-size: 1.25rem;
  font-weight: 700;
}
</style>
